import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  PlatformDefault,
  RatingRule,
  RiskProfile,
  RiskProfileCreatePayload,
  RiskProfileUpdatePayload,
} from '@/types/risk-profile';

export interface UseRiskProfilesResult {
  profiles: RiskProfile[];
  platformDefault: PlatformDefault | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createProfile: (payload: RiskProfileCreatePayload) => Promise<RiskProfile>;
  updateProfile: (id: string, payload: RiskProfileUpdatePayload) => Promise<RiskProfile>;
  deleteProfile: (id: string) => Promise<void>;
  /** Edit the platform default (creating its row on the first edit). */
  savePlatformDefault: (
    rules: RatingRule[],
    description?: string | null,
    highRiskJurisdictions?: string[]
  ) => Promise<PlatformDefault>;
  /** The shipped rules — what "reset to default" restores. */
  loadBuiltinDefault: () => Promise<PlatformDefault>;
}

/**
 * Admin-only: the named risk profiles and the platform default. `enabled`
 * false skips the fetch (the config editor only needs the list when the
 * caller is an admin).
 */
export function useRiskProfiles(options: { enabled?: boolean } = {}): UseRiskProfilesResult {
  const enabled = options.enabled ?? true;
  const [profiles, setProfiles] = useState<RiskProfile[]>([]);
  const [platformDefault, setPlatformDefault] = useState<PlatformDefault | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!enabled) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const [list, fallback] = await Promise.all([
        vnextApiClient.listRiskProfiles(),
        vnextApiClient.getPlatformDefaultRiskProfile(),
      ]);
      setProfiles(list);
      setPlatformDefault(fallback);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load risk profiles');
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  /** Run a write, refresh the lists, and surface a failure as a toast. */
  const mutate = useCallback(
    async <T>(title: string, action: () => Promise<T>, refresh = true): Promise<T> => {
      try {
        const result = await action();
        if (refresh) {
          await refetch();
        }
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(title);
        notifications.show({ title, message: e.message, color: 'red' });
        throw e;
      }
    },
    [refetch]
  );

  return {
    profiles,
    platformDefault,
    isLoading,
    error,
    refetch,
    createProfile: (payload) =>
      mutate('Could not create the risk profile', () => vnextApiClient.createRiskProfile(payload)),
    updateProfile: (id, payload) =>
      mutate('Could not save the risk profile', () =>
        vnextApiClient.updateRiskProfile(id, payload)
      ),
    deleteProfile: (id) =>
      mutate('Could not delete the risk profile', () => vnextApiClient.deleteRiskProfile(id)),
    savePlatformDefault: (rules, description, highRiskJurisdictions) =>
      mutate('Could not save the platform default', () =>
        vnextApiClient.putPlatformDefaultRiskProfile({
          rules,
          description,
          high_risk_jurisdictions: highRiskJurisdictions ?? [],
        })
      ),
    loadBuiltinDefault: () =>
      mutate(
        'Could not load the built-in default',
        () => vnextApiClient.getBuiltinDefaultRiskProfile(),
        false
      ),
  };
}
